<template>
  <div class="elv-financial-process-table">
    <ag-grid-vue
      v-if="processTableConfig.length > 0"
      :style="{
        height: props.height,
        minWidth: '406px',
        maxWidth: '100%'
      }"
      class="elv-ag-theme-alpine"
      :grid-options="gridOptions"
      :enable-cell-change-flash="false"
      :column-defs="processTableConfig"
      :loading-cell-renderer="TableLoadingCell"
      :loading-cell-renderer-params="loadingCellRendererParams"
      :is-server-side-group-open-by-default="isServerSideGroupOpenByDefault"
      :get-row-id="getRowId"
      :get-row-height="getRowHeight"
      :pinned-bottom-row-data="pinnedBottomRowData"
      @cellClicked="onCellClicked"
      @grid-ready="onGridReady"
      @first-data-rendered="onFirstDataRendered"
    />
    <AccountOrContactDrawer
      ref="overlayDrawerRef"
      :drawer-data="overlayDrawerData.currentData"
      :is-batch="overlayDrawerData.isBatch"
      :drawer-title="overlayDrawerData.drawerTitle"
      :table-type="overlayDrawerData.type"
      :total-contacts="overlayDrawerData.totalContacts"
      @resetList="resetList"
      @onUpdateAccountOrContact="onUpdateAccountOrContact"
    />

    <TransactionEditOverlay
      ref="transactionEditOverlayRef"
      :is-batch="overlayDrawerData.isBatch"
      :current-data="overlayDrawerData.currentData"
      @resetList="resetList"
    />
  </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { emitter } from '@/lib/mitt'
import { ElMessage } from 'element-plus'
import { AgGridVue } from 'ag-grid-vue3'
import { LicenseManager } from 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css' // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css' // Optional theme CSS
import { sumBy, filter, isEmpty, capitalize, differenceWith } from 'lodash-es'

import ReportsApi from '@/api/ReportsApi'
import noDataImg from '@/assets/img/noData.png'
import useInitPageIntro from '@/hooks/initPageIntro'
import { useReportStore } from '@/stores/modules/reports/index'
import AccountOrContactDrawer from './AccountOrContactDrawer.vue'
import CustomTooltip from '@/components/Reports/Cell/Tooltips.vue'
import TableLoadingCell from '@/components/Reports/Cell/TableLoadingCell.vue'
import TransactionEditOverlay from '../Transactions/components/EditOverlay.vue'
import { useReportsTableConfigStore } from '@/stores/modules/reports/tableConfig'

LicenseManager.setLicenseKey(import.meta.env.VITE_TABLE_KEY)

const props = defineProps({
  type: {
    type: String,
    required: true
  },
  height: {
    type: String,
    default: `calc(100vh - 219px)`
  },
  groupName: {
    type: String,
    default: 'name'
  },
  groupCapitalize: {
    type: Boolean,
    default: false
  }
})
const { t } = useI18n()
const route = useRoute()

const reportStore = useReportStore()
// eslint-disable-next-line no-unused-vars
const { initPageIntro } = useInitPageIntro()
const tableConfigStore = useReportsTableConfigStore()
const { processTableConfig } = storeToRefs(tableConfigStore)
const overlayDrawerRef = ref()
const columnData = ref()
const tableDataList: any = ref([])
const transactionEditOverlayRef = ref()
const gridApi: any = ref(null)
const gridColumnApi: any = ref(null)

const overlayDrawerData: any = reactive({
  type: 'contact',
  drawerTitle: 'Contact',
  currentData: {},
  isBatch: false,
  totalContacts: 0
})

const loadingCellRendererParams = reactive({
  loadingMessage: 'Loading'
})
const groupLitsData: any = reactive({
  group: {},
  listData: {}, // 分组后list的数据
  total: {}, // 总计数据
  totalCount: 0
})

// ag-grid基础配置
const gridOptions: any = reactive({
  sidebar: false,
  tooltipShowDelay: 500,
  headerHeight: 35,
  // tooltipHideDelay: 5000,
  // columnHoverHighlight: true,
  rowModelType: 'serverSide',
  groupDisplayType: 'custom', // custom || groupRows
  suppressRowTransform: true,
  suppressContextMenu: true,
  animateRows: true,
  debounceVerticalScrollbar: false,
  suppressPropertyNamesCheck: true,
  rowGroupPanelShow: true,
  // serverSideInfiniteScroll: true, // 设置服务器端行模型是否使用无限滚动
  maxConcurrentDatasourceRequests: 1, // 有多少请求同时命中服务器。如果达到最大值，请求将排队
  cacheBlockSize: 50, // 缓存中每个块有多少行，即一次从服务器返回多少行
  blockLoadDebounceMillis: 300, // 加载块之前等待多少毫秒。在无限滚动和滚动许多无限块时很有用，因为它可以防止块加载直到滚动结束。
  serverSideInitialRowCount: 0, // 初始化从第几行开始显示
  defaultColDef: {
    resizable: true,
    suppressMovable: true,
    cellStyle: {
      height: '42px',
      'line-height': '42px' // 行高设置同步：跟rowHeight属性设置值一致
    },
    rowHeight: 42,
    tooltipComponent: CustomTooltip,
    tooltipValueGetter: (params: any) => {
      return params?.value
    }
  },
  overlayLoadingTemplate: '<span class="ag-overlay-loading-center">data is loading...</span>',
  overlayNoRowsTemplate: `<div class="elv-result">
    <img src="${noDataImg}" style="width: 40px; height: 40px;margin-bottom:16px" />
    <div class="elv-result-description">
      No data matches this filter
    </div>
  </div>`
})

const isGroup = computed(() => {
  return props.type !== 'categorized' && props.type !== 'review' && props.type !== 'contactsUnnamed'
})

const currentEntityPermission = computed(() => {
  return reportStore.entityPermission()
})

const entityId = computed(() => {
  return Number(route.params?.entityId)
})

const onCheckBatchTransactions = () => {
  overlayDrawerData.isBatch = true
  overlayDrawerData.type = 'transaction'
  overlayDrawerData.currentData = {}
  transactionEditOverlayRef.value?.onCheckDrawerStatus()
}

const onFirstDataRendered = (params: any) => {
  if (props.type === 'categorized' || props.type === 'review') {
    nextTick(() => {
      params?.columnApi?.autoSizeColumns([
        'transactions-date-time',
        'transactions-source',
        'transactions-currency',
        'transactions-amount-in',
        'transactions-amount-out',
        'transactions-event'
      ])
      const windowWidth = window.innerWidth
      if (windowWidth >= 1650) {
        params?.api?.sizeColumnsToFit({
          columnLimits: [{ key: 'transactions-from-to' }]
        })
      } else {
        params?.columnApi?.autoSizeColumn({ key: 'transactions-from-to' })
      }
    })
  }
}

const onDeleteTableRow = async (rowIds: string[]) => {
  if (!rowIds.length) return
  rowIds.forEach((itemId) => {
    const rowNode = gridApi.value.getRowNode(itemId)
    const rowNodeElement: any = document.querySelectorAll(`[row-id="${rowNode.id}"]`)
    if (rowNode && rowNodeElement) {
      rowNode.setRowHeight(0)
      rowNode.setDisplayed(false)
      gridApi.value.applyServerSideTransaction({
        remove: [{ rowId: rowNode.data.rowId }]
      })
      rowNodeElement[0].style.setProperty('display', 'none', 'important')
      rowNodeElement[0].style.setProperty('border', 'none', 'important')
      rowNodeElement[0].style.setProperty('overflow', 'hidden', 'important')
      rowNodeElement[1].style.setProperty('display', 'none', 'important')
      rowNodeElement[1].style.setProperty('overflow', 'hidden', 'important')
      rowNodeElement[1].style.setProperty('border', 'none', 'important')
    }
    tableDataList.value = []
    gridApi.value.refreshServerSide()
  })
  groupLitsData.totalCount -= rowIds.length
  groupLitsData.total.count = groupLitsData.totalCount
  // gridApi.value.setRowCount(groupLitsData.totalCount)
  if (props.type === 'review') {
    reportStore.fetchIssueDataList(entityId.value)
    const { data } = await ReportsApi.getActivityEvent(entityId.value, { status: 'REVIEWED' })
    const transactionFilter = {
      ...reportStore.transactionFilter,
      categoryNos: data
    }
    reportStore.processTableListDataInit(entityId.value, 'reviewed', { limit: 10, page: 1, ...transactionFilter })
  }
}

const onEditTableRowEvent = (data: any) => {
  if (!data.rows.length) return
  data.rows.forEach((item: any) => {
    const rowNode = gridApi.value.getRowNode(item.rowId)
    if (rowNode) {
      rowNode?.setData({ ...rowNode.data, categoryNo: data.categoryNo })
      groupLitsData.listData?.list.forEach((listItem: any) => {
        if (listItem.rowId === item.rowId) {
          // eslint-disable-next-line no-param-reassign
          listItem.categoryNo = data.categoryNo
        }
      })
    }
  })
  reportStore.checkTransactionsAll = false
}

emitter.on('onEditTableRowEvent', onEditTableRowEvent)
emitter.on('onDeleteTableRow', onDeleteTableRow)

// 单元格点击事件
const onCellClicked = (cell: any) => {
  console.log(cell)
  columnData.value = cell
  overlayDrawerData.currentData = {}
  if (
    cell.value?.tableType === 'transactions' &&
    cell.value?.cellName === 'From/To' &&
    (cell.event?.target?.className === 'elv-financials-cell-fromTo-main__address' ||
      cell.event?.target?.className === 'elv-financials-cell-fromTo-item-main__logo' ||
      cell.event?.target?.className?.baseVal === 'elv-financials-cell-fromTo-item-main__logo' ||
      cell.event?.target?.className === 'elv-financials-cell-fromTo-item-main')
  ) {
    const direction = cell.event?.target?.attributes['data-type'].value
    switch (direction) {
      case 'from':
        if (cell.data?.fromEntityAccount?.address || cell.data?.fromEntityAccount?.cardNumber) {
          overlayDrawerData.drawerTitle = 'Account'
          overlayDrawerData.currentData = {
            paymentIdentifier: cell.data?.fromAddress,
            source: { ...cell.data.source },
            platform: { ...cell.data?.fromEntityAccount?.platform },
            ...cell.data?.fromEntityAccount,
            drawerType: 'fromEntityAccount'
          }
        } else if (cell.data?.fromAddressContact?.role) {
          overlayDrawerData.drawerTitle = 'Contact'
          overlayDrawerData.currentData = {
            paymentIdentifier: cell.data?.fromAddress,
            source: { ...cell.data.source },
            ...cell.data?.fromAddressContact,
            platform: cell.data?.platform,
            drawerType: 'fromAddressContact'
          }
        } else {
          switch (cell.data?.direction) {
            case 'OUT':
              overlayDrawerData.drawerTitle = 'Account'
              overlayDrawerData.currentData = {
                paymentIdentifier: cell.data?.fromAddress,
                platform: cell.data?.platform,
                source: { ...cell.data.source },
                drawerType: 'fromEntityAccount',
                entityAccountId: cell.data?.toEntityAccountId ?? cell.data?.fromEntityAccountId
              }
              break
            default:
              overlayDrawerData.drawerTitle = 'Contact'
              overlayDrawerData.currentData = {
                paymentIdentifier: cell.data?.fromAddress,
                platform: cell.data?.platform,
                source: { ...cell.data.source },
                platformId:
                  cell.data.source?.type === 'CSV' && cell.data.source?.csvFile.templateType === 'BANK'
                    ? 31
                    : cell.data?.platformId,
                drawerType: 'fromAddressContact'
              }
              break
          }
        }
        break
      default:
        if (cell.data?.toEntityAccount?.address) {
          overlayDrawerData.drawerTitle = 'Account'
          overlayDrawerData.currentData = {
            paymentIdentifier: cell.data?.toAddress,
            source: { ...cell.data.source },
            platform: { ...cell.data?.toEntityAccount?.platform },
            ...cell.data?.toEntityAccount,
            drawerType: 'toEntityAccount'
          }
        } else if (cell.data?.toAddressContact?.role) {
          overlayDrawerData.drawerTitle = 'Contact'
          overlayDrawerData.currentData = {
            paymentIdentifier: cell.data?.toAddress,
            source: { ...cell.data.source },
            platform: cell.data?.platform,
            ...cell.data?.toAddressContact,
            drawerType: 'toAddressContact'
          }
        } else {
          switch (cell.data?.direction) {
            case 'IN':
              overlayDrawerData.drawerTitle = 'Account'
              overlayDrawerData.currentData = {
                paymentIdentifier: cell.data?.toAddress,
                source: { ...cell.data.source },
                drawerType: 'toEntityAccount',
                platform: cell.data?.platform,
                entityAccountId: cell.data?.toEntityAccountId ?? cell.data?.fromEntityAccountId
              }
              break
            default:
              overlayDrawerData.drawerTitle = 'Contact'
              overlayDrawerData.currentData = {
                paymentIdentifier: cell.data?.toAddress,
                platform: cell.data?.platform,
                source: { ...cell.data.source },
                platformId:
                  cell.data.source?.type === 'CSV' && cell.data.source?.csvFile.templateType === 'BANK'
                    ? 31
                    : cell.data?.platformId,
                drawerType: 'toAddressContact'
              }
              break
          }
        }
        break
    }
    overlayDrawerData.isBatch = false
    overlayDrawerData.type = 'transaction'
    overlayDrawerRef.value?.onCheckDrawerStatus()
  } else if (
    cell.value?.tableType === 'transactions' &&
    cell.event?.target?.className !== 'elv-financials-cell-date-time-main-bottom__copy' &&
    cell?.rowPinned !== 'bottom' &&
    cell?.column?.pinned === null
  ) {
    if (!currentEntityPermission.value?.transaction?.viewDetail) {
      ElMessage.warning(t('message.noPermission'))
      return
    }
    overlayDrawerData.isBatch = false
    overlayDrawerData.currentData = cell.data
    transactionEditOverlayRef.value?.onCheckDrawerStatus()
  } else if (
    (((cell.value?.tableType === 'accounts' || cell.value?.tableType === 'contactsNamed') && cell.node?.level !== 0) ||
      cell.value?.tableType === 'contactsUnnamed') &&
    cell.event?.target?.className !== 'elv-financials-cell-address-main__copy'
  ) {
    if (cell.value?.tableType === 'accounts') {
      overlayDrawerData.drawerTitle = 'Account'
      overlayDrawerData.type = 'account'
    } else {
      overlayDrawerData.drawerTitle = 'Contact'
      overlayDrawerData.type = 'contact'
    }
    overlayDrawerData.isBatch = false
    overlayDrawerData.currentData = cell.data
    overlayDrawerRef.value?.onCheckDrawerStatus()
  }
  overlayDrawerData.currentData.rowId = cell.node.id
}

// 分组数据处理
const groupRowDataFormatter = async (params: any) => {
  let data: any = {
    list: [],
    total: {}
  }
  if (!groupLitsData.group?.list?.length) {
    data = await reportStore.processTableDataInit(entityId.value, props.type)

    if (props.type === 'accounts' || props.type === 'contactsNamed') {
      groupLitsData.group.list = data
      groupLitsData.listData = data
      const list = data
      data = { list }
    } else if (props.type === 'holdings') {
      groupLitsData.group.list = data.holdingValues
      groupLitsData.listData = data.holdingValues
      const list = data.holdingValues
      const costBasisUsd = sumBy(data.holdingValues, (item: any) => item.costBasis)
      const marketValueUsd = sumBy(data.holdingValues, (item: any) => item.marketValue)
      groupLitsData.total = {
        costBasis: costBasisUsd,
        marketValue: marketValueUsd
      }
      data = { list }
    } else {
      groupLitsData.group = data
      groupLitsData.total = data.total
    }
    if (data?.totalCount) groupLitsData.totalCount = data.totalCount
  } else if (params.request?.endRow && params.request.startRow !== 0 && params.request.endRow !== 100) {
    if (params.request.endRow / 30) {
      data.list = groupLitsData.group?.list?.slice(params.request.startRow, params.request.endRow)
    } else {
      data.list = groupLitsData.group?.list?.slice(0, 30)
    }
  } else {
    data.list = groupLitsData.group?.list.slice(0, 30)
  }
  let rowData: any = []
  // 一级分组
  if (data?.list?.length > 0) {
    data?.list.forEach((firstLevelGroupData: any, groupOneIndex: number) => {
      const tableFirstGroupData: any = []
      tableFirstGroupData.push({
        ...firstLevelGroupData,
        index: groupOneIndex,
        groupLevel: 1,
        label: props.groupCapitalize
          ? capitalize(firstLevelGroupData?.[props.groupName])
          : firstLevelGroupData?.[props.groupName],
        [`firstLevelGroup`]: props.groupCapitalize
          ? capitalize(firstLevelGroupData?.[props.groupName])
          : firstLevelGroupData?.[props.groupName]
      })
      rowData = [...rowData, ...tableFirstGroupData]
    })
  }
  reportStore.loading = false
  return rowData
}

// 获取列表数据
const getTableGroupListData = async (params: any) => {
  let groupListParams: any = {
    page: 1,
    limit: 100
  }
  let tableGroupListData: any = []
  if (props.type === 'general-ledger') {
    groupListParams.paramsId = params.parentNode?.data?.accountId
    groupListParams.limit = 30
  } else {
    groupListParams = {
      page: 1,
      limit: 50
    }
    groupListParams.page = params.request.endRow / 50 ? Number((params.request.endRow / 50).toFixed(0)) : 1
  }
  if (props.type === 'categorized' || props.type === 'review') {
    groupListParams = {
      ...groupListParams,
      ...reportStore.batchReviewParams
    }
    if (!isEmpty(reportStore.batchReviewParams)) {
      groupListParams.page = 1
      groupListParams.limit = 100
    }
  }
  if (
    props.type === 'general-ledger' ||
    props.type === 'categorized' ||
    props.type === 'contactsUnnamed' ||
    (props.type === 'review' && isEmpty(reportStore.batchReviewParams)) ||
    isEmpty(groupLitsData.listData)
  ) {
    groupLitsData.listData = await reportStore.processTableListDataInit(entityId.value, props.type, groupListParams)

    if (props.type === 'categorized' || props.type === 'review') {
      if (!isEmpty(reportStore.batchReviewParams)) {
        groupLitsData.listData.totalCount =
          groupLitsData.listData.totalCount >= 100 ? 100 : groupLitsData.listData.totalCount
      }
      // reportStore.checkTransactionsAll = false
      groupLitsData.total = groupLitsData.listData.total
      groupLitsData.total.count = groupLitsData.listData.totalCount
      groupLitsData.totalCount = groupLitsData.listData.totalCount
    }
    if (props.type === 'general-ledger') {
      groupLitsData.totalCount = groupLitsData.listData.list?.length
    }
    if (props.type === 'contactsUnnamed') {
      groupLitsData.totalCount = groupLitsData.listData.total
    }
  }
  let list: any = []
  if (props.type !== 'accounts' && props.type !== 'contactsNamed' && props.type !== 'holdings') {
    list = groupLitsData.listData?.list ? groupLitsData.listData?.list : groupLitsData.listData
  } else if (props.type === 'holdings') {
    list = groupLitsData.listData[params?.parentNode?.data?.index]?.platformValues
    groupLitsData.totalCount = list.length
  } else {
    list = groupLitsData.listData[params?.parentNode?.data?.index]?.list
    groupLitsData.totalCount = list.length
  }
  list?.forEach((item: any, index: number) => {
    item.rowId = `${index}${
      // eslint-disable-next-line no-nested-ternary
      item?.financialGeneralLedgerId
        ? item?.financialGeneralLedgerId
        : item?.activityId
        ? item?.activityId
        : `${index}${Math.floor(Math.random() * 900) + 100}`
    }`
  })
  const filterList = props.type === 'categorized' ? filter(list, (item: any) => item?.type !== 'GAIN_LOSS') : list
  if (
    props.type === 'categorized' ||
    props.type === 'contactsNamed' ||
    (props.type === 'review' && isEmpty(reportStore.batchReviewParams))
  ) {
    if (reportStore.checkTransactionsAll) {
      reportStore.batchEditSelected.push(...filterList)
    }
    if (tableDataList.value.length === 0 || tableDataList.value.length <= params.request.endRow) {
      tableDataList.value = [...tableDataList.value, ...list]
    } else {
      const newList = tableDataList.value.splice(
        params.request.startRow,
        params.request.endRow - params.request.startRow,
        ...list
      )
      tableDataList.value = newList
    }
    reportStore.checkTransactionsAll =
      tableDataList.value.length === reportStore.batchEditSelected.length && tableDataList.value.length > 0
    reportStore.transactionTotal = tableDataList.value.length
  } else if (!isEmpty(reportStore.batchReviewParams) && reportStore.batchEditSelected.length === 0) {
    reportStore.checkTransactionsAll =
      reportStore.unSelectedList.length === 1 || reportStore.unSelectedList.length === 0
    if (reportStore.checkTransactionsAll) {
      reportStore.batchEditSelected.push(...filterList)
    } else {
      reportStore.batchEditSelected = differenceWith(
        list,
        reportStore.unSelectedList,
        (a: any, b: any) => a.activityId === b
      )
    }
    tableDataList.value = [...list]
    reportStore.transactionTotal = tableDataList.value.length
  }
  tableGroupListData =
    props.type !== 'categorized' &&
    props.type !== 'review' &&
    props.type !== 'general-ledger' &&
    props.type !== 'contactsUnnamed' &&
    !isEmpty(reportStore.batchReviewParams)
      ? list?.slice(params.request.startRow, params.request.endRow)
      : list
  return tableGroupListData
}

// 获取服务器列表数据
const getServerSideDataSource = () => {
  gridApi.value.setServerSideDatasource({
    // eslint-disable-next-line consistent-return
    getRows: async (params: any) => {
      try {
        loadingCellRendererParams.loadingMessage = 'Loading'
        // 获取tableList和tableGroup数据
        if (!isGroup.value) {
          // 未分组list
          gridOptions.maxConcurrentDatasourceRequests = 2
          gridOptions.cacheBlockSize = 2
          gridApi.value.hideOverlay()
          const tableGroupListData = await getTableGroupListData(params)
          let lastRow: number = -1
          if (groupLitsData.totalCount < params.request.endRow) {
            lastRow = groupLitsData.totalCount
          }
          if (params.request.endRow === undefined || params.request.startRow === undefined) {
            lastRow = groupLitsData.totalCount
          }
          if ((props.type === 'categorized' || props.type === 'review') && groupLitsData.totalCount > 0) {
            lastRow = groupLitsData.totalCount
          }
          if (Number(groupLitsData.listData?.list?.length) === 0 && props.type !== 'review') {
            lastRow = 0
            gridApi.value?.showNoRowsOverlay()
          }
          reportStore.loading = false
          params.successCallback(tableGroupListData, lastRow)
          if (props.type === 'review' && groupLitsData.listData?.list?.length && params.request.endRow === 100) {
            nextTick(() => {
              setTimeout(() => {
                if (reportStore.entityDetail?.guideStep > 0) {
                  const steps = [
                    {
                      element: '.elv-report-transactions-tab .el-tabs__nav ', // 这是添加引导的元素id
                      intro: t('bubbleGuide.forReview.step1'), // 这是引导提示内容
                      position: 'right' // 这是引导位置
                    },
                    {
                      element: '.elv-table-operating-forReview-single.el-tooltip__trigger',
                      intro: t('bubbleGuide.forReview.step2'),
                      position: 'left'
                    },
                    {
                      element: '.elv-table-operating-forReview-rule.el-tooltip__trigger',
                      intro: t('bubbleGuide.forReview.step3'),
                      position: 'left'
                    }
                  ]
                  initPageIntro(steps, 'transactions-for-review')
                }
              }, 500)
            })
          }
        } else if (isEmpty(params.request.groupKeys)) {
          // 一级分组框架
          gridOptions.maxConcurrentDatasourceRequests = 1
          gridOptions.cacheBlockSize = 30
          gridApi.value.hideOverlay()

          const groupRowData = await groupRowDataFormatter(params)
          let lastRow: number = -1
          const tableGroupDataTotal: number = groupLitsData.group?.list?.length ? groupLitsData.group?.list.length : 0
          if (tableGroupDataTotal < params.request.endRow) {
            lastRow = Number(tableGroupDataTotal)
          }
          if (params.request.endRow === undefined || params.request.startRow === undefined) {
            lastRow = Number(tableGroupDataTotal)
          }
          if (Number(tableGroupDataTotal) === 0) {
            lastRow = 0
            gridApi.value.showNoRowsOverlay()
          }
          params.successCallback(groupRowData, lastRow)
        } else {
          gridOptions.cacheBlockSize = 30
          // 一级分组下的list
          const tableGroupListData = await getTableGroupListData(params)
          params.success({
            rowData: tableGroupListData,
            rowCount: groupLitsData.totalCount
          })
        }
      } catch (error) {
        console.log(error)
        loadingCellRendererParams.loadingMessage = 'ERR'
        params.fail()
      }
    }
  })
}

// ag-gridAPI初始化
const onGridReady = async (params: any) => {
  // 获取gridApi
  gridApi.value = params.api
  gridColumnApi.value = params.columnApi
  reportStore.setAgGridApi(params?.api, params?.columnApi)
  // 获取服务器列表数据
  getServerSideDataSource()
}

const onCheckAllTransactions = () => {
  // eslint-disable-next-line no-nested-ternary
  reportStore.batchEditSelected = reportStore.checkTransactionsAll
    ? props.type !== 'contactsNamed'
      ? [...unref(tableDataList.value)]
      : groupLitsData.listData?.list
    : []
  if (props.type === 'categorized') {
    reportStore.batchEditSelected = filter(reportStore.batchEditSelected, (item: any) => item?.type !== 'GAIN_LOSS')
  }
}
emitter.on('onCheckAllTransactions', onCheckAllTransactions)

// 刷新列表
const resetList = (checkAll: boolean = false) => {
  groupLitsData.group = {}
  groupLitsData.listData = {}
  groupLitsData.total = {}
  tableDataList.value = []
  groupLitsData.totalCount = 0
  reportStore.checkTransactionsAll = checkAll
  if (!isEmpty(reportStore.batchReviewParams)) {
    gridOptions.cacheBlockSize = 100
  }
  gridApi.value?.ensureIndexVisible(0, 'top')
  gridApi.value?.refreshServerSide({ route: [], purge: true })
}
emitter.on('resetList', resetList)

const onUpdateAccountOrContact = (data: any) => {
  overlayDrawerData.currentData = data
}
// 当前行唯一标识
const getRowId = (params: any) => {
  // eslint-disable-next-line no-nested-ternary
  const rowId = params.data?.label
    ? `${params.data?.label}${params.data?.index}`
    : // eslint-disable-next-line no-nested-ternary
    params?.parentKeys
    ? `${params?.parentKeys[0]}${params.data?.rowId}`
    : params.data?.rowId
    ? `${params.data?.rowId}`
    : `${params.data?.rowId}${Math.floor(Math.random() * 900) + 100}`
  return rowId
}
// eslint-disable-next-line no-unused-vars
const getRowHeight = (params: any) => {
  if ((params.node.level === 0 || params.node.level === 1) && params.data?.type === 'total') {
    return 80
  }
  return 42
}
// 是否展开分组
const isServerSideGroupOpenByDefault = (params: any) => {
  if (props.type === 'holdings' && params?.data.groupLevel < 2) return true
  return false
}

// 底部行数据
const pinnedBottomRowData = computed(() => {
  let fieldValues
  if (isGroup.value) {
    fieldValues = groupLitsData.total
  } else {
    // eslint-disable-next-line no-unused-vars
    fieldValues = groupLitsData.total
  }
  return props.type === 'categorized' || props.type === 'review' || props.type === 'holdings'
    ? [{ fieldValues, defaultAggregation: '', name: '' }]
    : null
})

defineExpose({ groupLitsData, onCheckBatchTransactions })

onBeforeUnmount(() => {
  emitter.off('resetList', resetList)
  emitter.off('onCheckAllTransactions', onCheckAllTransactions)
  emitter.off('onDeleteTableRow', onDeleteTableRow)
  emitter.off('onEditTableRowEvent', onEditTableRowEvent)
})
</script>

<style scoped lang="scss">
.elv-ag-theme-alpine {
  --ag-background-color: transparent;
  --ag-foreground-color: #0e1420;
  --ag-header-foreground-color: rgb(162, 174, 186);
  --ag-header-background-color: #f9fbfe;
  --ag-odd-row-background-color: transparent;
  // --ag-header-cell-hover-background-color: rgba(255, 255, 255 , 1)
  // --ag-odd-row-background-color: rgba(248, 250, 254, 0.5);
  --ag-font-size: 14px;
  // --ag-row-height: 42px;
  // --ag-line-height: 42px;
  --ag-list-item-height: 50px;
  --ag-header-column-resize-handle-height: 0%;
  --ag-header-column-resize-handle-width: 1px;
  --ag-grid-size: 5px;
  --ag-borders: 1px solid;
  --ag-border-color: #e4e7eb;
  --ag-row-border-color: #e4e7eb;
  // --ag-font-family: 'Barlow-Regular';
  --ag-borders-row: solid 1px;
  --ag-cell-horizontal-border: solid #e4e7eb;
  --ag-header-column-separator-display: block;
}

:deep(.ag-root-wrapper) {
  border-color: #cedee0;
}

:deep(.ag-horizontal-left-spacer, .ag-horizontal-right-spacer) {
  overflow-x: overlay;
}

:deep(.ag-body-horizontal-scroll-viewport) {
  overflow-x: overlay;
}

:deep(.ag-header-group-cell-with-group) :deep(.ag-header-cell-label) {
  color: rgb(44, 46, 48);
  font-size: x-small;
  justify-content: right;
}

:deep(.ag-header) {
  height: 36px !important;
  min-height: 36px !important;
  border-bottom-color: #cedee0;
}

:deep(.ag-header-row):nth-child(1) {
  height: 36px !important;
  line-height: 34px;
  font-size: 11px;
  font-family: 'Plus Jakarta Sans';
  font-weight: 500;

  color: $elv-color-9BA8B5;
  //   letter-spacing: 0.075em;
  background-color: #eef4fb;
}

:deep(.ag-pinned-left-header),
:deep(.ag-pinned-left-cols-container) {
  box-shadow: 6px 0px 12px rgba(0, 0, 0, 0.05), 1px 0px 4px rgba(23, 83, 235, 0.1);
  z-index: 100;
}

.ag-floating-top .ag-row {
  background-color: #000;
}

:deep(.ag-floating-bottom-viewport) {
  background: #f9fafb;
  height: 52px;

  .ag-row-pinned {
    height: 100% !important;
  }

  .ag-cell {
    height: 52px !important;
  }
}

.ag-floating-bottom .ag-row {
  background-color: #000;
}

:deep(.ag-header-cell) {
  color: $elv-color-E4E7EB;
  padding-left: 0;
  padding-right: 0;
}

:deep(.ag-overlay) {
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0) 1%, rgba(255, 255, 255, 1));
}

:deep(.ag-floating-bottom-full-width-container) {
  height: 52px;
}

:deep(.ag-floating-bottom) {
  height: 52px !important;
  min-height: 52px !important;
  border-top-color: #cedee0;

  .ag-cell {
    font-family: 'Barlow' !important;
    font-weight: 700 !important;
    font-size: 12px !important;
  }
}
// 隐藏工具面板
:deep(.ag-side-buttons) {
  display: none;
}

.elv-financial-process-table {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  background: url('@/assets/img/screener-watermark.png') no-repeat 58%;
  background-size: 631px 200px;
  margin-left: -1px;
  padding-top: 10px;
}

:deep(.ag-row) {
  .ag-cell {
    border-left-width: 0px;
    border-top-width: 0px;
    border-bottom-width: 0px;
    display: flex;
    align-items: center;
  }
}

:deep(.ag-header-group-cell-label) {
  justify-content: center;
}

:deep(.el-button.is-text:not(.is-disabled):active) {
  background-color: $elv-color-F9FBFE;
}

:deep(.ag-theme-alpine .ag-tabs-header) {
  display: none;
}

:deep(.ag-theme-alpine .ag-tabs-body) {
  width: 150px;
  box-sizing: border-box;
}

:deep(.ag-tabs) {
  min-width: auto;
}

:deep(.ag-row-group) {
  height: 42px;
  line-height: 42px;

  .ag-cell-value {
    height: 42px !important;
    line-height: 42px !important ;
  }

  .ag-cell {
    font-family: 'Barlow' !important;
    font-weight: 700 !important;
  }
}

:deep(.ag-row-level-0) {
  .ag-cell-value {
    &:has(.elv-table-group-rows) {
      padding-left: 11px;
    }

    &:has(.elv-financials-total) {
      height: 80px !important;
    }

    &.ag-cell-first-right-pinned {
      &:has(.elv-financials-cell-event-main.is-pinned):hover {
        background: #edf0f3;
      }
    }
  }

  &:hover {
    background: #f9fafb;

    .elv-ag-index-select {
      display: flex;
    }
  }
}

:deep(.ag-row-level-0.ag-row-group) {
  background: $elv-color-FCF3E3;
}

:deep(.ag-row-level-1.ag-row-group) {
  background: $elv-color-EFF4FF;
}

:deep(.ag-row-level-2.ag-row-group) {
  background: $elv-color-F5FEF4;
}

:deep(.ag-row-level-1) {
  .elv-table-group-rows-content__label {
    background: $elv-color-DCE6FF;
    border: 1px solid $elv-color-C4D6FF;
  }

  .ag-cell-value {
    &:has(.elv-ag-group-index) {
      border-right: 0px;
    }

    &:has(.elv-financials-total) {
      height: 80px !important;
    }

    &:has(.elv-table-group-rows) {
      padding-left: 11px;
      padding-right: 0px;
    }
  }
}

:deep(.ag-row-level-2) {
  .elv-ag-index {
    display: none;
  }

  .elv-table-group-rows-content__label {
    background: $elv-color-D7F7D2;
    border: 1px solid $elv-color-CCEAC7;
  }

  .ag-cell-value {
    &:has(.elv-ag-index) {
      border-right: 0px;
    }

    &:has(.elv-ag-group-index) {
      border-right: 0px;
    }

    &:has(.elv-table-group-rows) {
      padding-left: 35px;
      padding-right: 0px;
    }
  }
}

:deep(.ag-row-level-3) {
  .elv-ag-index {
    display: none;
  }

  .ag-cell-value {
    &:has(.elv-ag-index) {
      border-right: 0px;
    }

    &:has(.elv-table-group-rows__index) {
      padding-left: 38px;
    }
  }
}

:deep(.ag-row-group-contracted) {
  &:first-of-type {
    .ag-cell-value {
      padding-top: 0px;
    }
  }

  .ag-cell {
    border-width: 0px;
    border-right-width: 1px;
  }

  .elv-ag-group-arrow {
    height: 42px;
  }
}

:deep(.ag-pinned-right-floating-bottom) {
  box-shadow: 6px 0px 12px rgba(0, 0, 0, 0.05), 1px 0px 4px rgba(23, 83, 235, 0.1);

  .ag-row {
    background-color: #f9fafb;
    height: 52px !important;
  }

  .ag-cell-value {
    height: 52px !important;

    &:nth-child(1) {
      border-right: 0px;
    }
  }
}

:deep(.ag-pinned-right-header) {
  box-shadow: 6px 0px 12px rgba(0, 0, 0, 0.05), 1px 0px 4px rgba(23, 83, 235, 0.1);
}

:deep(.ag-pinned-right-cols-container) {
  position: relative;

  &::before {
    position: absolute;
    width: 4px;
    height: 100%;
    content: '';
    left: -4px;
    top: 0px;
    background: linear-gradient(270deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0) 100%);
  }
}

:deep(.ag-row-group-expanded) {
  .ag-cell {
    border-left-width: 0px;
    border-top-width: 0px;
  }
}

:deep(.ag-ltr .ag-cell-focus:not(.ag-cell-range-selected):focus-within) {
  border: none;
  border-right: 1px solid $elv-color-E4E7EB !important;
}

:deep(.ag-header-cell-comp-wrapper) {
  height: 34px;
}

:deep(.ag-pinned-left-header
    .ag-header-row-column-group
    .ag-header-group-cell-with-group:nth-child(2)
    .ag-header-group-cell-label) {
  font-family: 'Plus Jakarta Sans';
  font-weight: 500;
  font-size: 11px;
  transform: scale(0.91);
  line-height: 14px;
  color: $elv-color-9BA8B5;
  justify-content: flex-start !important;
  text-transform: lowercase;
}
</style>
